import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FloppyDisk, ArrowClockwise } from "@phosphor-icons/react";

const WEIGHT_KEYS = ["distance", "qualifications", "reliability", "rating", "history"];

export default function AdminMatching() {
  const [settings, setSettings] = useState(null);
  const [audit, setAudit] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, a] = await Promise.all([
      api.get("/matching/settings"),
      api.get("/matching/audit?limit=100"),
    ]);
    setSettings(s.data);
    setAudit(a.data || []);
  };
  useEffect(() => { load(); }, []);

  const setWeight = (k, v) => setSettings((s) => ({ ...s, weights: { ...s.weights, [k]: parseFloat(v) || 0 } }));
  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v === "" ? "" : Number(v) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        weights: settings.weights,
        offer_timeout_s: settings.offer_timeout_s,
        search_ring_size: settings.search_ring_size,
        max_candidates: settings.max_candidates,
        max_radius_km_default: settings.max_radius_km_default,
      };
      const r = await api.put("/matching/settings", payload);
      setSettings(r.data);
      toast.success("Settings saved (weights auto-normalized).");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-10 label-caps">Loading…</div>;

  const totalW = WEIGHT_KEYS.reduce((s, k) => s + (Number(settings.weights?.[k]) || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-matching-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Matching settings</h1>
        <p className="text-sm text-slate-600 mt-1">Tune the smart matcher for your business. Weights auto-normalize on save.</p>
      </div>

      <div className="card-tactile p-6">
        <div className="label-caps text-slate-500 mb-3">Weights (relative importance)</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {WEIGHT_KEYS.map((k) => (
            <div key={k}>
              <Label htmlFor={`w-${k}`} className="capitalize">{k}</Label>
              <Input id={`w-${k}`} data-testid={`weight-${k}`} type="number" step="0.05" min="0" max="1"
                value={settings.weights?.[k] ?? 0}
                onChange={(e) => setWeight(k, e.target.value)} />
            </div>
          ))}
        </div>
        <div className="text-xs text-slate-500 mt-2">Sum: <b className={Math.abs(totalW - 1) < 0.001 ? "text-emerald-700" : "text-amber-700"}>{totalW.toFixed(2)}</b> (will be normalized to 1.0 on save)</div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="tout">Offer timeout (s)</Label>
            <Input id="tout" data-testid="offer-timeout" type="number" min="10" max="300" value={settings.offer_timeout_s} onChange={(e) => setField("offer_timeout_s", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ring">H3 search ring</Label>
            <Input id="ring" data-testid="search-ring" type="number" min="1" max="10" value={settings.search_ring_size} onChange={(e) => setField("search_ring_size", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="maxc">Max candidates</Label>
            <Input id="maxc" data-testid="max-candidates" type="number" min="1" max="200" value={settings.max_candidates} onChange={(e) => setField("max_candidates", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="maxr">Default radius (km)</Label>
            <Input id="maxr" data-testid="max-radius" type="number" step="0.5" min="0.5" max="200" value={settings.max_radius_km_default} onChange={(e) => setField("max_radius_km_default", e.target.value)} />
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <Button data-testid="save-settings" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700"><FloppyDisk size={16} className="mr-1.5"/> {saving ? "Saving…" : "Save settings"}</Button>
          <Button variant="outline" onClick={load}><ArrowClockwise size={16} className="mr-1.5"/> Reload</Button>
        </div>
      </div>

      <div className="card-tactile p-6">
        <div className="label-caps text-slate-500 mb-3">Recent dispatch events</div>
        {audit.length === 0 && <div className="text-sm text-slate-500 py-4">No events yet.</div>}
        <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
          {audit.map((a) => (
            <div key={a.id} className="text-xs flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0">
              <span className="font-mono text-slate-400 w-40 shrink-0">{new Date(a.created_at).toLocaleString()}</span>
              <span className="font-bold w-40 shrink-0 text-slate-900">{a.event}</span>
              <span className="text-slate-600 truncate">{a.job_id ? `job=${a.job_id.slice(0,8)}` : ""} {a.worker_id ? `· worker=${a.worker_id.slice(0,8)}` : ""} {a.data && Object.keys(a.data).length > 0 ? `· ${JSON.stringify(a.data).slice(0, 120)}` : ""}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
